(() => {
  if (customElements.get('tabs-component')) return;

  /**
   * Tabs component for interactive tab navigation.
   *
   * @extends {HTMLElement}
   */
  class TabsComponent extends HTMLElement {
    /** @type {number} */
    #currentIndex = 0;

    /**
     * Gets all tab buttons.
     * @returns {NodeListOf<HTMLButtonElement>}
     */
    get #buttons() {
      return this.querySelectorAll('[role="tab"]');
    }

    /**
     * Gets all tab panels.
     * @returns {NodeListOf<HTMLElement>}
     */
    get #panels() {
      return this.querySelectorAll('[role="tabpanel"]');
    }

    constructor() {
      super();
      // Bind event handlers
      this.handleTabClick = this.handleTabClick.bind(this);
    }

    /**
     * Called when the element is connected to the DOM.
     */
    connectedCallback() {
      // Set initial state
      this.#currentIndex = Array.from(this.#buttons).findIndex(
        (btn) => btn.getAttribute('aria-selected') === 'true'
      );
      if (this.#currentIndex === -1) this.#currentIndex = 0;

      this.setEventListeners();
    }

    /**
     * Called when the element is disconnected from the DOM.
     */
    disconnectedCallback() {
      this.removeEventListeners();
    }

    /**
     * Set up event listeners.
     */
    setEventListeners() {
      this.#buttons.forEach((button) => {
        button.addEventListener('click', this.handleTabClick);
      });
    }

    /**
     * Remove event listeners.
     */
    removeEventListeners() {
      this.#buttons.forEach((button) => {
        button.removeEventListener('click', this.handleTabClick);
      });
    }

    /**
     * Handles tab click events.
     * @param {Event} event - The click event.
     */
    handleTabClick(event) {
      event.preventDefault();
      const button = event.currentTarget;
      const index = parseInt(button.dataset.tabIndex, 10);
      this.selectTab(index);
    }

    /**
     * Selects a tab by index.
     * @param {number} index - The index of the tab to select.
     */
    selectTab(index) {
      if (index < 0 || index >= this.#buttons.length) return;

      this.#currentIndex = index;

      // Update all buttons and panels
      this.#buttons.forEach((button, i) => {
        const isSelected = i === index;
        button.setAttribute('aria-selected', isSelected.toString());
        button.setAttribute('tabindex', isSelected ? '0' : '-1');
      });

      this.#panels.forEach((panel, i) => {
        const isActive = i === index;
        panel.hidden = !isActive;
        panel.classList.toggle('tabs__panel--active', isActive);
      });

      // Focus the selected button
      this.#buttons[index]?.focus();
    }
  }

  customElements.define('tabs-component', TabsComponent);
})();
